import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CourseTrack, QuizQuestion, QuizSubmission } from '../../shared/models';
import { AcademyQuizService } from '../../services/academy-quiz.service';
import { AcademyNavigationService } from '../../services/academy-navigation.service';
import { QuizQuestionComponent } from '../quiz-question/quiz-question.component';
import {
  isPartOfLearningTrack,
  learningTrackHeaderTitle,
  learningTrackCoursesCompleted,
  learningTrackCoursesTotal,
} from '../../shared/course-track.utils';

@Component({
  selector: 'ut-academy-quiz-container',
  templateUrl: './quiz-container.component.html',
  styleUrls: ['./quiz-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuizContainerComponent implements OnInit {
  @ViewChild(QuizQuestionComponent) questionComponent?: QuizQuestionComponent;

  @Input() trackId: number | null = null;
  @Input() useRedesignCatalog = false;

  courseTrack: CourseTrack | null = null;
  currentStep = 0;
  userChoices: QuizSubmission = [];
  isLoading = true;
  isSubmitting = false;
  error: string | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private academyQuizService: AcademyQuizService,
    private academyNavigationService: AcademyNavigationService,
  ) {}

  get isPartOfLearningTrack(): boolean {
    return isPartOfLearningTrack(this.courseTrack, this.useRedesignCatalog);
  }

  get isLockedByOrder(): boolean {
    return !!this.courseTrack?.user_is_locked_by_learning_track_order;
  }

  get headerTitle(): string {
    return learningTrackHeaderTitle(this.courseTrack, this.useRedesignCatalog);
  }

  get trackCoursesCompleted(): number {
    return learningTrackCoursesCompleted(this.courseTrack);
  }

  get trackCoursesTotal(): number {
    return learningTrackCoursesTotal(this.courseTrack);
  }

  ngOnInit(): void {
    if (this.trackId) {
      this.loadQuizData(this.trackId);
    } else {
      this.error = 'No track ID provided';
      this.isLoading = false;
    }
  }

  private loadQuizData(trackId: number): void {
    this.academyQuizService.getTrackWithQuiz(trackId).subscribe({
      next: (courseTrack) => {
        if (courseTrack.user_is_locked_by_learning_track_order) {
          this.courseTrack = courseTrack;
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        if (!courseTrack.user_can_take_quiz) {
          const firstLesson = courseTrack.courses?.[0];
          if (firstLesson) {
            this.academyNavigationService.goToLesson(courseTrack, firstLesson.slug, { replace: true });
          }
          return;
        }

        this.courseTrack = courseTrack;
        this.initializeUserChoices();
        this.isLoading = false;
        this.focusCurrentQuestion();
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = 'Failed to load quiz data';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private initializeUserChoices(): void {
    this.userChoices = [];
    this.questions.forEach(q => {
      this.userChoices.push({ question: q.id, answer: undefined });
    });
  }

  // Helper to handle focus after DOM updates
  private focusCurrentQuestion(): void {
    setTimeout(() => {
      this.questionComponent?.focusQuestion();
    });
  }

  get quiz() {
    return this.courseTrack?.quiz;
  }

  get questions(): QuizQuestion[] {
    return this.quiz?.questions || [];
  }

  get totalQuestions(): number {
    return this.questions.length;
  }

  get currentQuestion(): QuizQuestion | null {
    return this.questions[this.currentStep] || null;
  }

  get currentQuestionNumber(): number {
    return this.currentStep + 1;
  }

  get isFirstQuestion(): boolean {
    return this.currentStep === 0;
  }

  get isLastQuestion(): boolean {
    return this.currentStep === this.totalQuestions - 1;
  }

  get hasSelectedAnswer(): boolean {
    if (!this.currentQuestion) return false;
    const choice = this.userChoices.find(c => c.question === this.currentQuestion!.id);
    return choice?.answer !== undefined;
  }

  get selectedChoiceId(): number | null {
    if (!this.currentQuestion) return null;
    const choice = this.userChoices.find(c => c.question === this.currentQuestion!.id);
    return choice?.answer ?? null;
  }

  get canContinue(): boolean {
    return this.hasSelectedAnswer;
  }

  onAnswerSelected(choiceId: number): void {
    if (this.currentQuestion) {
      const choice = this.userChoices.find(c => c.question === this.currentQuestion!.id);
      if (choice) {
        choice.answer = choiceId;
        this.cdr.markForCheck();
      }
    }
  }

  goToPrevious(): void {
    if (!this.isFirstQuestion) {
      this.currentStep--;
      this.focusCurrentQuestion();
      this.cdr.markForCheck();
    }
  }

  goToNext(): void {
    if (this.canContinue && !this.isLastQuestion) {
      this.currentStep++;
      this.focusCurrentQuestion();
      this.cdr.markForCheck();
    }
  }

  submitQuiz(): void {
    if (!this.canContinue || !this.courseTrack || this.isSubmitting || !this.trackId) return;

    this.isSubmitting = true;
    this.cdr.markForCheck();

    this.academyQuizService.submitQuiz(this.trackId, this.userChoices).subscribe({
      next: (response) => {
        const quizStorageKey = `courseTrackQuiz${this.trackId}`;
        const userChoicesWithAnswers = {
          courseTrackId: this.trackId,
          userChoice: response,
        };
        localStorage.setItem(quizStorageKey, JSON.stringify(userChoicesWithAnswers));

        this.academyNavigationService.goToReportCard(this.courseTrack!);
      },
      error: () => {
        this.error = 'Failed to submit quiz. Please try again.';
        this.isSubmitting = false;
        this.cdr.markForCheck();
      },
    });
  }

  onBackToTracks(): void {
    this.academyNavigationService.goToAcademyHome();
  }

  onLessonClick(lesson: any): void {
    if (this.courseTrack && lesson?.slug) {
      this.academyNavigationService.goToLesson(this.courseTrack, lesson.slug);
    }
  }
}
